import React from "react";
import { Box, Button, Card, CardContent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AccountRoutePaths } from "../routes";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";
import colors from "../../shared/themes/colors";
import { dt } from "../../shared/themes/redesignTokens";
import { useBreakpoint } from "../../shared/hooks/useBreakpoint";
import useTranslation from "../../shared/hooks/useTranslation";
import BookDemoForm from "./BookDemoForm";

/**
 * What `/account/sign-up` (and `/account/confirm-registration`) render on the `.com` app hosts
 * instead of the registration flow — HOST_POLICY_SPEC §6 FE-B, owner decision 2026-09-01:
 * MachineID is invite-only on `.com`, new companies are onboarded personally.
 *
 * This is UX only. The server refuses those endpoints off the `.cc` surface regardless
 * (403 `REGISTRATION_NOT_AVAILABLE_ON_HOST` from Customers); this page exists so a visitor gets an
 * explanation and a next step instead of a form that 403s.
 *
 * The card frame deliberately matches `StartRegistrationPage` — same logo, same width, same
 * padding — so a visitor who followed a sign-up link does not feel they landed on a different
 * product. New markup is Tailwind + `dt` tokens (canonical decision 1); only the outer
 * MUI `Box`/`Card` frame is reused verbatim from the page it replaces.
 */
interface InviteOnlyPageProps {
  /**
   * Set by the two registration pages when the SERVER refused registration on this host (403
   * `REGISTRATION_NOT_AVAILABLE_ON_HOST`), i.e. the server classified this hostname as `.com`.
   * Forwarded to the Book-a-Demo form so the lead goes to the `.com` landing origin even on a
   * hostname the client's `COM_APP_HOSTS` list does not know (Codex C-2).
   *
   * Omitted when this page is rendered from the ROUTE (`useAccountRoutes`), where the only signal
   * available IS the client host check — that path keeps its existing behaviour.
   */
  refusedSurface?: "com";
}

const InviteOnlyPage = ({ refusedSurface }: InviteOnlyPageProps) => {
  const navigate = useNavigate();
  const { translate } = useTranslation("account");
  const { isPhone } = useBreakpoint();

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        paddingY: isPhone ? "32px" : "64px",
        backgroundColor: colors.secondary.gray98,
      }}
    >
      <Card
        sx={{
          maxWidth: "600px",
          width: "100%",
          margin: "0 16px",
          display: "flex",
          flexDirection: "column",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          padding: isPhone ? "32px 16px 32px" : "64px",
          gap: "32px",
        }}
      >
        <Box className="flex flex-col items-center text-center">
          <MachineLogoIcon />
          <h1
            className="mt-4 text-center"
            style={{
              color: colors.primary.violetDark,
              fontSize: isPhone ? "28px" : "36px",
              lineHeight: isPhone ? "34px" : "42px",
              letterSpacing: "-0.072px",
              fontWeight: 500,
            }}
          >
            {translate("inviteOnly.title")}
          </h1>
          <p className="mt-4 text-base" style={{ color: dt.textSecondary }}>
            {translate("inviteOnly.body")}
          </p>
        </Box>

        <CardContent sx={{ padding: 0 }}>
          <h2 className="mb-4 text-lg font-medium" style={{ color: dt.textPrimary }}>
            {translate("inviteOnly.bookDemo")}
          </h2>

          <BookDemoForm refusedSurface={refusedSurface} />

          <Box className="mt-6 flex items-center justify-center">
            <Box component="span" className="text-16-regular" sx={{ color: colors.secondary.gray20 }}>
              {translate("alreadyMember")}
            </Box>
            <Button variant="text" onClick={() => navigate(AccountRoutePaths.signIn)}>
              <Box className="text-16-regular" sx={{ textDecoration: "underline", color: dt.blue }}>
                {translate("logIn")}
              </Box>
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InviteOnlyPage;
